import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useState } from "react";
import { useFragment } from "react-relay";

import { Avatar } from "@whoosh/design-system/src/components/display";
import {
  HStack,
  Pressable,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { useFormatClubCurrency } from "@whoosh/features/src/ClubCurrency";

import { EventSignupSummaryPlayerList_event$key } from "./__generated__/EventSignupSummaryPlayerList_event.graphql";
import type { EventSignupSummaryPlayerList_signUp$data } from "./__generated__/EventSignupSummaryPlayerList_signUp.graphql";
import { EventSignupSummaryPlayerList_signUp$key } from "./__generated__/EventSignupSummaryPlayerList_signUp.graphql";

type Props = {
  eventFragmentRef: EventSignupSummaryPlayerList_event$key;
  signUpFragmentRef: EventSignupSummaryPlayerList_signUp$key;
  isPublicEvent: boolean;
};

type RegistrationEdge = NonNullable<
  NonNullable<
    NonNullable<
      EventSignupSummaryPlayerList_signUp$data["registrations"]
    >["edges"]
  >[number]
>;

type RegistrationNode = NonNullable<RegistrationEdge["node"]>;

type FormResponseEdge = NonNullable<
  NonNullable<NonNullable<RegistrationNode["formResponses"]>["edges"]>[number]
>;

type FormResponseNode = NonNullable<FormResponseEdge["node"]>;

const getFormResponseDisplayValue = (
  response: FormResponseNode
): string | null => {
  if (response.booleanValue != null)
    return response.booleanValue ? t`Yes` : t`No`;
  if (response.stringValue) return response.stringValue;
  return null;
};

export const EventSignupSummaryPlayerList = ({
  eventFragmentRef,
  signUpFragmentRef,
  isPublicEvent,
}: Props) => {
  const formatPrice = useFormatClubCurrency();
  const event = useFragment(
    EventSignupSummaryPlayerListEventFragment,
    eventFragmentRef
  );
  const signUp = useFragment(
    EventSignupSummaryPlayerListSignUpFragment,
    signUpFragmentRef
  );

  const [expanded, setExpanded] = useState(false);

  const registrations = signUp?.registrations?.edges ?? [];
  const eventName = event.eventSeries.name;
  const isPerSignUpEvent = event.primaryChargeType === "PER_SIGN_UP";
  const displayedChargeTypeIds = new Set<string>();

  if (!registrations.length) return null;

  return (
    <VStack gap={4} testID="event-signup-summary-player-list">
      <VStack gap={2}>
        <Text variant="mainSmallSemiBold" color="text-secondary">
          {t`Event Signup`}
        </Text>
        <HStack alignItems="center" gap={2}>
          <Text variant="mainTitle3" flex={1}>
            {eventName}
          </Text>
          <Pressable
            onPress={() => setExpanded((v) => !v)}
            accessibilityLabel={expanded ? t`View less` : t`View more`}
            testID="event-signup-summary-view-toggle"
          >
            <Text variant="mainTiny" color="text-secondary">
              {expanded ? t`VIEW LESS` : t`VIEW MORE`}
            </Text>
          </Pressable>
        </HStack>
      </VStack>

      <VStack gap={1}>
        {registrations.map((edge) => {
          const registration = edge?.node;
          if (!registration) return null;

          const registrant = registration.registrant;
          const name =
            registrant?.firstName && registrant?.lastName
              ? `${registrant.firstName} ${registrant.lastName}`
              : registration.unknownRegistrantDisplayName ?? t`Unknown Player`;

          const isGuest =
            registration.type === "GUEST" ||
            !!registration.unknownRegistrantDisplayName;
          const isCustomer =
            registration.__typename === "CustomerEventRegistration";
          const avatarType = isCustomer
            ? "customer"
            : isGuest
            ? "guest"
            : "member";

          const price =
            registration.primaryChargeType?.chargeRuleSet?.chargeRules
              ?.edges?.[0]?.node?.price ?? 0;
          const chargeTypeName =
            registration.primaryChargeType?.chargeRuleSet?.displayName ?? null;
          const chargeTypeId = registration.primaryChargeType?.id ?? null;
          const shouldDisplaySignupCharge =
            !isPerSignUpEvent ||
            chargeTypeId === null ||
            !displayedChargeTypeIds.has(chargeTypeId);

          if (isPerSignUpEvent && chargeTypeId !== null) {
            displayedChargeTypeIds.add(chargeTypeId);
          }

          const shouldDisplayPrice = price > 0 && shouldDisplaySignupCharge;

          const formResponses =
            registration.formResponses?.edges
              ?.map((e) => e?.node)
              .filter((n): n is NonNullable<typeof n> => n != null) ?? [];

          return (
            <VStack key={registration.id}>
              <HStack
                alignItems="center"
                justifyContent="space-between"
                minHeight={10}
                gap={3}
                paddingVertical={2}
              >
                <HStack flex={1} alignItems="center" gap={3}>
                  <Avatar
                    size="xs"
                    profileImagePublicId={registrant?.profileImage?.publicId}
                    firstName={registrant?.firstName}
                    lastName={registrant?.lastName}
                    type={avatarType}
                  />
                  <VStack flex={1}>
                    <Text variant="mainBaseSemiBold">{name}</Text>
                    {!expanded &&
                    isPublicEvent &&
                    chargeTypeName &&
                    shouldDisplaySignupCharge ? (
                      <Text variant="mainSmall" color="text-secondary">
                        {chargeTypeName}
                      </Text>
                    ) : null}
                  </VStack>
                </HStack>
                {shouldDisplayPrice ? (
                  <Text variant="mainBaseSemiBold">{formatPrice(price)}</Text>
                ) : null}
              </HStack>

              {expanded ? (
                <VStack paddingLeft={9} paddingBottom={4}>
                  {shouldDisplayPrice ? (
                    <HStack
                      alignItems="center"
                      gap={2}
                      paddingVertical={2}
                      minHeight={8}
                    >
                      <Text variant="mainBaseMedium" flex={1}>
                        {chargeTypeName ?? t`Entry Fee`}
                      </Text>
                      <Text variant="mainBaseMedium">{formatPrice(price)}</Text>
                    </HStack>
                  ) : null}
                  {formResponses.map((response) => {
                    const displayValue = getFormResponseDisplayValue(response);
                    if (!displayValue) return null;
                    const questionLabel =
                      response.questionConfiguration?.prompt ?? t`Question`;
                    return (
                      <HStack
                        key={response.id}
                        alignItems="center"
                        gap={2}
                        paddingVertical={2}
                        minHeight={8}
                      >
                        <Text variant="mainBaseMedium" flex={1}>
                          {questionLabel}
                        </Text>
                        <Text variant="mainBaseMedium" textAlign="right">
                          {displayValue}
                        </Text>
                      </HStack>
                    );
                  })}
                </VStack>
              ) : null}
            </VStack>
          );
        })}
      </VStack>
    </VStack>
  );
};

export const EventSignupSummaryPlayerListEventFragment = graphql`
  fragment EventSignupSummaryPlayerList_event on Event {
    primaryChargeType
    eventSeries {
      name
    }
  }
`;

export const EventSignupSummaryPlayerListSignUpFragment = graphql`
  fragment EventSignupSummaryPlayerList_signUp on EventSignUp {
    registrations(first: 50) {
      edges {
        node {
          id
          __typename
          type
          ... on GuestEventRegistration {
            unknownRegistrantDisplayName
          }
          ... on CustomerEventRegistration {
            unknownRegistrantDisplayName
          }
          primaryChargeType {
            id
            chargeRuleSet {
              displayName
              chargeRules(first: 1) {
                edges {
                  node {
                    id
                    price
                  }
                }
              }
            }
          }
          registrant {
            id
            firstName
            lastName
            profileImage {
              # TODO(ENG-3436): Remove the publicId alias when legacy image props are retired.
              publicId: renderingKey
            }
          }
          formResponses(first: 50) {
            edges {
              node {
                id
                booleanValue
                stringValue
                questionConfiguration {
                  id
                  type
                  prompt
                }
              }
            }
          }
        }
      }
    }
  }
`;
